import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import { getEditableAttrs } from "./utils";
import { SectionHeader, SectionHeaderProps } from "./SectionHeader";
import { CardWithLink, CardWithLinkProps } from "./CardWithLink";
import { CardStats, CardStatsProps } from "./CardStats";
import { CardPlans, CardPlansProps } from "./CardPlans";
import { CardTestimonial, CardTestimonialProps } from "./CardTestimonial";
import { CardAlignable, CardAlignableProps } from "./CardAlignable";
import { CardFeatures, CardFeaturesProps } from "./CardFeatures";
import { CardArticle, CardArticleProps } from "./CardArticle";
import { sectionWrapper } from "./sectionWrappers";

export interface CardsGridProps
  extends Omit<ComponentProps<"section">, "title"> {
  header?: SectionHeaderProps | null;
  cards?:
    | (
        | (CardWithLinkProps & { cardType: "cardWithLink" })
        | (CardStatsProps & { cardType: "cardStats" })
        | (CardPlansProps & { cardType: "cardPlans" })
        | (CardTestimonialProps & { cardType: "cardTestimonial" })
        | (CardAlignableProps & { cardType: "cardAlignable" })
        | (CardFeaturesProps & { cardType: "cardFeatures" })
        | (CardArticleProps & { cardType: "cardArticle" })
      )[]
    | null;
  layout?: "one-cards" | "two-cards" | "three-cards" | "four-cards";
  backgroundColor?: "none" | "light" | "accent" | "accent-light";
  topPadding?: "none" | "default" | "large";
  bottomPadding?: "none" | "default";
  imagesAspectRatio?: string;
}

export const CardsGrid: React.FC<CardsGridProps> = ({
  header,
  cards,
  layout,
  backgroundColor = "none",
  topPadding = "none",
  bottomPadding = "none",
  imagesAspectRatio = "3/2",
  className,
  id,
  ...props
}) => {
  return (
    <section
      id={id}
      className={sectionWrapper({ backgroundColor, topPadding, bottomPadding })}
      {...props}
    >
      <div className="uipm:max-w-[1440px] uipm:mx-auto uipm:md:px-[var(--container-margin)]">
        <div className={cn("uipm:font-[var(--font-family-sans)]", className)}>
          {header && (
            <div className="uipm:mb-[var(--container-title-gap)] uipm:px-[var(--container-margin)] uipm:md:px-0">
              <SectionHeader {...header} />
            </div>
          )}

          {/* Cards Section */}
          {cards && cards.length > 0 && (
            <div
              className={cn(
                "uipm:grid uipm:gap-[var(--container-gutter)]",
                layout === "one-cards" && "uipm:grid-cols-1",
                layout === "two-cards" &&
                  "uipm:grid-cols-1 uipm:md:grid-cols-2",
                layout === "three-cards" &&
                  "uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:lg:grid-cols-3",
                layout === "four-cards" &&
                  "uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:lg:grid-cols-4",
                !layout &&
                  "uipm:grid-cols-1 uipm:md:grid-cols-2 uipm:lg:grid-cols-3"
              )}
            >
              {cards.map((card, index) => {
                const { cardType, ...cardProps } = card;
                return (
                  <div
                    key={index}
                    className="uipm:h-full"
                    {...getEditableAttrs(card)}
                  >
                    {(() => {
                      switch (cardType) {
                        case "cardStats":
                          return (
                            <CardStats
                              key={index}
                              {...(cardProps as CardStatsProps)}
                            />
                          );
                        case "cardPlans":
                          return (
                            <CardPlans
                              key={index}
                              {...(cardProps as CardPlansProps)}
                            />
                          );
                        case "cardTestimonial":
                          return (
                            <CardTestimonial
                              key={index}
                              {...(cardProps as CardTestimonialProps)}
                            />
                          );
                        case "cardAlignable":
                          return (
                            <CardAlignable
                              key={index}
                              {...(cardProps as CardAlignableProps)}
                            />
                          );
                        case "cardFeatures":
                          return (
                            <CardFeatures
                              key={index}
                              {...(cardProps as CardFeaturesProps)}
                            />
                          );
                        case "cardArticle": {
                          const articleProps = cardProps as CardArticleProps;
                          return (
                            <CardArticle
                              key={index}
                              {...articleProps}
                              image={
                                articleProps.image
                                  ? {
                                      ...articleProps.image,
                                      aspectRatio: imagesAspectRatio,
                                    }
                                  : undefined
                              }
                            />
                          );
                        }
                        case "cardWithLink":
                        default:
                          return (
                            <CardWithLink
                              key={index}
                              {...(cardProps as CardWithLinkProps)}
                            />
                          );
                      }
                    })()}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};
